import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { TournamentClock } from '@/components/Clock';
import { PlayersList, AddPlayersForm } from '@/components/Players';
import { RoundsManager } from '@/components/Tournament/RoundsManager';
import { TournamentSettings } from '@/components/Tournament/TournamentSettings';
import { TournamentStats } from '@/components/Tournament/TournamentStats';
import clsx from 'clsx';
import { 
  Clock, 
  Users, 
  Settings, 
  BarChart3, 
  Timer,
  Trophy,
  Target,
  HelpCircle
} from 'lucide-react';

type TabType = 'clock' | 'players' | 'rounds' | 'prizes' | 'settings' | 'stats';

export const TournamentDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('clock');
  const tournament = useSelector((state: RootState) => state.tournament.current);
  const players = useSelector((state: RootState) => state.players.players);
  const { isRunning } = useSelector((state: RootState) => state.clock);

  const tabs = [
    { id: 'clock' as const, label: 'Tournament Clock', icon: Clock },
    { id: 'players' as const, label: 'Players', icon: Users, badge: players.length },
    { id: 'rounds' as const, label: 'Rounds', icon: Timer, badge: tournament?.rounds.length },
    { id: 'prizes' as const, label: 'Prizes', icon: Trophy, badge: tournament?.prizes.length },
    { id: 'settings' as const, label: 'Settings', icon: Settings },
    { id: 'stats' as const, label: 'Statistics', icon: BarChart3 },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'clock':
        return (
          <div className="space-y-6">
            <TournamentClock className="max-w-4xl mx-auto" />
            <TournamentStats />
          </div>
        );
      case 'players':
        return (
          <div className="space-y-6">
            <AddPlayersForm />
            <PlayersList />
          </div>
        );
      case 'rounds':
        return <RoundsManager />;
      case 'prizes':
        return (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Trophy size={24} />
              Prize Structure
            </h2>
            <p className="text-gray-600">Prize management coming soon...</p>
          </div>
        );
      case 'settings':
        return <TournamentSettings />;
      case 'stats':
        return (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <BarChart3 size={24} />
              Tournament Statistics
            </h2>
            <p className="text-gray-600">Detailed statistics coming soon...</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-poker-green rounded-full flex items-center justify-center">
                <Target size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Tournament Director</h1>
                {tournament && (
                  <p className="text-sm text-gray-600">{tournament.settings.name}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Tournament Status */}
              <div className="flex items-center gap-2">
                <div className={clsx(
                  'w-3 h-3 rounded-full',
                  {
                    'bg-green-500 animate-pulse': isRunning,
                    'bg-gray-400': !isRunning,
                  }
                )} />
                <span className="text-sm text-gray-600">
                  {isRunning ? 'Running' : 'Paused'}
                </span>
              </div>
              
              {/* Help */}
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <HelpCircle size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                    {
                      'border-blue-500 text-blue-600': isActive,
                      'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300': !isActive,
                    }
                  )}
                >
                  <Icon size={18} />
                  {tab.label}
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className={clsx(
                      'inline-flex items-center justify-center px-2 py-1 text-xs font-bold rounded-full',
                      {
                        'bg-blue-100 text-blue-800': isActive,
                        'bg-gray-100 text-gray-800': !isActive,
                      }
                    )}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {renderTabContent()}
      </main>
    </div>
  );
};